import apiClient from "@shared/services/apiClient";

export type ArchivalStatus =
  | "level0_archived"
  | "fully_archived"
  | "restoring"
  | "restored"
  | "level0_purged"
  | null;

export interface ArchivalStatusResponse {
  archival_status: ArchivalStatus;
  restore_ready_at: string | null;
  archived_at: string | null;
  restore_estimated_minutes: number | null;
  purge_eligible: boolean;
  purge_block_reason: string | null;
  restore_expires_at: string | null;
  archival_hold: boolean;
  archival_hold_reason: string | null;
  archival_hold_set_at: string | null;
}

export interface ArchivalResult {
  success: boolean;
  message: string;
  status?: ArchivalStatus;
}

export interface RestoreResponse {
  status?: string;
  estimatedMinutes?: number;
  message?: string;
  data?: ArchivalResult;
}

export interface BatchArchiveResult {
  results: Array<{ recording_id: string; success: boolean; message: string }>;
  succeeded: number;
  total: number;
}

export type ArchiveScope = "level0" | "raw_and_level0" | "full_bundle" | "full";

export async function archiveRecording(
  recordingId: string,
  scope: ArchiveScope = "level0",
): Promise<ArchivalResult> {
  const res = await apiClient.post<{ data: ArchivalResult }>(
    `/eeg/recordings/${recordingId}/archive`,
    { scope },
  );
  return res.data.data;
}

export async function restoreRecording(
  recordingId: string,
): Promise<RestoreResponse> {
  const res = await apiClient.post<RestoreResponse>(
    `/eeg/recordings/${recordingId}/restore`,
  );
  return res.data;
}

export async function getArchivalStatus(
  recordingId: string,
): Promise<ArchivalStatusResponse> {
  const res = await apiClient.get<{ data: ArchivalStatusResponse }>(
    `/eeg/recordings/${recordingId}/archival-status`,
  );
  return res.data.data;
}

export async function purgeLevel0(
  recordingId: string,
  dryRun = false,
): Promise<ArchivalResult> {
  const res = await apiClient.post<{ data: ArchivalResult }>(
    `/eeg/recordings/${recordingId}/purge-level0`,
    { dryRun },
  );
  return res.data.data;
}

export async function archiveBatch(params: {
  recording_ids: string[];
  scope?: ArchiveScope;
  older_than_days?: number;
}): Promise<BatchArchiveResult> {
  const res = await apiClient.post<{ data: BatchArchiveResult }>(
    `/eeg/recordings/archive-batch`,
    params,
  );
  return res.data.data;
}

export interface ArchivalHoldResult {
  success: boolean;
  message: string;
  archival_hold: boolean;
}

export async function setArchivalHold(
  recordingId: string,
  hold: boolean,
  reason?: string,
): Promise<ArchivalHoldResult> {
  const res = await apiClient.put<{ data: ArchivalHoldResult }>(
    `/eeg/recordings/${recordingId}/hold`,
    { hold, reason },
  );
  return res.data.data;
}
